import React from 'react';
import { DefaultPalette, Stack } from '@fluentui/react';
import { CommandBar, ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IButtonStyles } from '@fluentui/react/lib/Button';
import { Text } from '@fluentui/react/lib/Text';
import i18next, { t } from 'i18next';
import { themeLight } from '../../../styles/colors';
import { buttonStylesFor, titleCommandBarButtonStyles } from '../../../styles/button-styles';
import { ITheme, useTheme } from '@fluentui/react';

interface IViewWrapperWithCommandBarBaseProps {
  items: ICommandBarItemProps[];
  title: string;
  overflowItems?: ICommandBarItemProps[];
  farItems?: ICommandBarItemProps[];
  overflowButtonStyles?: IButtonStyles;
  withBorderBottom?: boolean;
  titleBody?: string;
  children?: JSX.Element | undefined;
  useInBottom?: boolean;
}

const HighligthViewWrapperWithCommandBar: React.FC<IViewWrapperWithCommandBarBaseProps> = ({ ...props }) => {
  const theme: ITheme = useTheme();

  const backgroundStyle = {
    backgroundColor: theme.palette.white,
    height: '100%',
    color: theme.palette.neutralPrimary, // Color del texto
    boxShadow: 'black 1px 1.6px 3.6px 0px, rgba(0, 0, 0, 0.11) 0px 0.3px 0.9px 0px',
  }; // Usa el estilo generado

  const commandBarStyles = { height: '2rem' };

  const backgroundSecondaryStyles = { backgroundColor: theme.palette.white };

  const commandBarButtonSecondaryButtonStyles = buttonStylesFor({
    backgroundColor: theme.palette.neutralLighterAlt,
  });

  const items: ICommandBarItemProps[] = React.useMemo(() => {
    const ret: ICommandBarItemProps[] = [];

    props.items.forEach((item) => {
      ret.push(item);
    });
    return ret;
  }, [props.items]);

  return (
    <React.Fragment>
      <div className="full-width full-height" style={{ ...backgroundStyle }}>
        {items.length > 0 ? (
          <>
            <div
              className="full-width"
              style={{
                height: '6%',
                borderBottom: '1px solid ' + theme.palette.neutralQuaternary,
                marginBottom: 5,
              }}
            >
              <CommandBar
                items={items}
                overflowItems={props.overflowItems}
                overflowButtonProps={{ ariaLabel: 'Más opciones', styles: props.overflowButtonStyles }}
                farItems={props.farItems}
                ariaLabel="Inbox actions"
                primaryGroupAriaLabel="Email actions"
                farItemsGroupAriaLabel="More actions"
                className={props.withBorderBottom ? 'full-width border-bottom-nav' : 'full-width'}
                styles={{
                  root: { ...commandBarStyles, ...backgroundSecondaryStyles, padding: '10px' },
                }}
              />
            </div>
            <div>{props.children}</div>
            {props.useInBottom && (
              <div
                className="full-width"
                style={{
                  borderTop: '1px solid ' + theme.palette.neutralQuaternary,
                  marginTop: 5,
                }}
              >
                <CommandBar
                  farItems={props.items}
                  overflowItems={props.overflowItems}
                  overflowButtonProps={{ ariaLabel: 'Más opciones', styles: props.overflowButtonStyles }}
                  ariaLabel="Inbox actions"
                  primaryGroupAriaLabel="Email actions"
                  farItemsGroupAriaLabel="More actions"
                  className={props.withBorderBottom ? 'full-width border-top-nav' : 'full-width'}
                  styles={{
                    root: {
                      ...commandBarStyles,
                      ...backgroundSecondaryStyles,
                      padding: '10px',
                      marginBottom: 15,
                    },
                  }}
                  items={[]}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <div>{props.children}</div>
          </>
        )}
      </div>
    </React.Fragment>
  );
};

const highligthedUnstyledButtons = (items?: ICommandBarItemProps[]): ICommandBarItemProps[] | undefined => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const theme: ITheme = useTheme();

  const commandBarButtonSecondaryButtonStyles = buttonStylesFor({
    backgroundColor: theme.palette.neutralLighterAlt,
  });

  return (
    items &&
    items.map((item) => {
      const hasButtonStyles = !!item.buttonStyles;

      return {
        ...item,
        buttonStyles: hasButtonStyles ? item.buttonStyles : commandBarButtonSecondaryButtonStyles,
      };
    })
  );
};

const StandardViewWrapperWithCommandBar: React.FC<IViewWrapperWithCommandBarBaseProps> = ({ ...props }) => {
  const items = React.useMemo(() => highligthedUnstyledButtons(props.items), [props.items])!;
  const farItems = React.useMemo(() => highligthedUnstyledButtons(props.farItems), [props.farItems]);

  const theme: ITheme = useTheme();

  const commandBarStyles = { height: '2rem' };

  const backgroundSecondaryStyles = { backgroundColor: theme.palette.neutralLighterAlt };

  return (
    <Stack verticalFill>
      <div
        className="full-width view-wrapper-command-bar-wrapper"
        style={{
          borderBottom: '1px solid ' + theme.palette.neutralQuaternary,
          marginBottom: 5,
        }}
      >
        <CommandBar
          items={items}
          overflowItems={props.overflowItems}
          overflowButtonProps={{ ariaLabel: 'Más opciones', styles: props.overflowButtonStyles }}
          farItems={farItems}
          ariaLabel="Inbox actions"
          primaryGroupAriaLabel="Email actions"
          farItemsGroupAriaLabel="More actions"
          theme={theme}
          className="full-width"
          styles={{
            root: {
              // ...commandBarStyles,
              // ...backgroundSecondaryStyles,
              // padding: '0.4rem',
              // paddingLeft: '1rem',
            },
          }}
        />
      </div>
      {props.title && (
        <Text
          variant="xLarge"
          block
          nowrap
          styles={{ root: { fontWeight: 600, paddingLeft: '1rem', paddingTop: '1.5rem' } }}
        >
          {t(props.title)}
        </Text>
      )}
      {props.children}
      {props.useInBottom && (
        <div
          className="full-width"
          style={{
            borderTop: '1px solid ' + theme.palette.neutralQuaternary,
            marginTop: 5,
          }}
        >
          <CommandBar
            farItems={props.items}
            overflowItems={props.overflowItems}
            overflowButtonProps={{ ariaLabel: 'Más opciones', styles: props.overflowButtonStyles }}
            ariaLabel="Inbox actions"
            primaryGroupAriaLabel="Email actions"
            farItemsGroupAriaLabel="More actions"
            theme={theme}
            className={props.withBorderBottom ? 'full-width border-top-nav' : 'full-width'}
            styles={{
              root: {
                // ...commandBarStyles,
                // ...backgroundSecondaryStyles,
                // padding: '10px',
                // borderBottom: '1px solid #e0e0e0',
                // borderTop: '1px solid #e0e0e0',
                // backgroundColor: '#f6f6f6',
                marginBottom: 15,
              },
            }}
            items={[]}
          />
        </div>
      )}
    </Stack>
  );
};

interface IViewWrapperWithCommandBarProps extends IViewWrapperWithCommandBarBaseProps {
  variant?: 'standard' | 'highlight';
}

const ViewWrapperWithCommandBar: React.FC<IViewWrapperWithCommandBarProps> = ({
  variant = 'standard',
  ...props
}) => {
  const highligth = variant === 'highlight';

  return (
    <React.Fragment>
      {highligth ? (
        <HighligthViewWrapperWithCommandBar {...props} />
      ) : (
        <StandardViewWrapperWithCommandBar {...props} />
      )}
    </React.Fragment>
  );
};

export default ViewWrapperWithCommandBar;
